import { Controller } from "@hotwired/stimulus";

/**
 * The reschedule sheet on a fixture: a date and a time, saved by the sheet's own Save button.
 *
 * A button that says it saves had better be the thing that saves, and Cancel closes without
 * posting, so a change thought better of is not kept. The fields used to post on change in the
 * upcoming-matches list as well, where there was no button and no way to back out; that list is a
 * plain list of cards now and this runs only on the fixture's own page.
 *
 * The date is also drawn on the page behind the sheet, so a save that only reached the server left
 * the row underneath saying the old date until the page was reloaded. The row is updated here from
 * the values that were just posted: the alternative is a Turbo Stream off an endpoint that
 * currently answers with nothing, which is a change to the write path rather than to what the
 * screen shows.
 *
 * Every target but `form` is optional, which is what keeps the sheet working on a fixture that has
 * no date tile to update yet.
 */
export default class extends Controller<HTMLElement> {
  static override targets = [
    "dateDay",
    "dateMonth",
    "error",
    "form",
    "relative",
    "unscheduled",
    "when",
  ];

  declare readonly dateDayTarget: HTMLElement;
  declare readonly dateMonthTarget: HTMLElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly formTarget: HTMLFormElement;
  declare readonly hasDateDayTarget: boolean;
  declare readonly hasErrorTarget: boolean;
  declare readonly hasRelativeTarget: boolean;
  declare readonly hasUnscheduledTarget: boolean;
  declare readonly hasWhenTarget: boolean;
  declare readonly relativeTarget: HTMLElement;
  declare readonly whenTarget: HTMLElement;

  async submit(): Promise<void> {
    /* Any control that holds its value somewhere other than its own field - the time wheels
           do - writes it back now, before the form is read. */
    document.dispatchEvent(
      new CustomEvent("wp:commit", { detail: { root: this.element } }),
    );

    /* `URLSearchParams` and not `FormData`: a FormData body is sent as `multipart/form-data`,
           and Spring Security's CsrfFilter reads the token with `request.getParameter`, which for
           a multipart request answers nothing unless a MultipartFilter runs ahead of the security
           chain - and none does here. So the token was in the body, the filter could not see it,
           and every save was rejected and redirected to the login page. This form has no file in
           it and never needed the heavier encoding. */
    const response = await fetch(this.formTarget.action, {
      body: this.body(),
      method: "post",
    });

    /* A redirect means the save did not happen: `fetch` follows one transparently, so a
           rejected POST lands on the login page with a cheerful 200 and `ok` is true. Treating
           that as success is what made a save that never happened look like one that did. */
    const saved = response.ok && !response.redirected;

    if (this.hasErrorTarget) {
      this.errorTarget.hidden = saved;
    }

    if (saved) {
      this.reflect();
      this.formTarget.closest("dialog")?.close();
    }
  }

  /**
   * Puts the form back to what the server rendered, for a sheet that is being dismissed rather
   * than saved.
   *
   * Closing is not by itself discarding: the calendar and the wheels write into their fields as
   * they are used, and only the posting waits for Save. Without this, a date chosen and then
   * cancelled would still be sitting in the field the next time Save was pressed.
   */
  discard(): void {
    this.formTarget.reset();
    document.dispatchEvent(
      new CustomEvent("wp:paint", { detail: { root: this.element } }),
    );
  }

  /**
   * The form, encoded the way the server expects to read it.
   *
   * Built entry by entry rather than handing the `FormData` straight to `URLSearchParams`: the
   * two are only loosely compatible, and copying the string entries across says out loud that
   * this form holds no files - which is the assumption the encoding above depends on.
   */
  private body(): URLSearchParams {
    const body = new URLSearchParams();

    new FormData(this.formTarget).forEach((value, key) => {
      if (typeof value === "string") {
        body.append(key, value);
      }
    });

    return body;
  }

  /** The value of one of the form's own fields, or undefined when the form has no such field. */
  private field(name: string): string | undefined {
    const element = this.formTarget.elements.namedItem(name);

    return element instanceof HTMLInputElement ? element.value : undefined;
  }

  /**
   * Writes the saved date back onto the row behind the sheet.
   *
   * The time comes off the form rather than being formatted here, because that is the string the
   * server rendered too and re-deriving it in a second place is how the two come to disagree.
   * The month and the weekday are `Intl` in the page's own language, which is what the library's
   * own date formatting uses.
   */
  private reflect(): void {
    const date = this.field("scheduledAtDate");
    if (date === undefined || date === "") {
      return;
    }

    /* A fixture with no date yet is a different row - a clock and "date to be confirmed", not
           a date tile. Rebuilding that shape here would be the template written twice, so the
           server draws it and this asks for it. */
    if (this.hasUnscheduledTarget) {
      window.location.reload();

      return;
    }

    if (!this.hasDateDayTarget) {
      return;
    }

    const time = this.field("scheduledAtTime") || "12:00";
    const at = new Date(`${date}T${time}`);
    const locale = document.documentElement.lang || undefined;
    const weekday = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(
      at,
    );

    this.dateMonthTarget.textContent = new Intl.DateTimeFormat(locale, {
      month: "short",
    }).format(at);
    this.dateDayTarget.textContent = String(at.getDate());

    if (this.hasWhenTarget) {
      this.whenTarget.textContent = `${weekday.charAt(0).toLocaleUpperCase(locale)}${weekday.slice(1)} · ${time}`;
    }

    if (this.hasRelativeTarget) {
      this.relativeTarget.setAttribute("datetime", at.toISOString());

      /* The library owns what that element reads; this only changed the instant under it. */
      document.dispatchEvent(
        new CustomEvent("wp:paint", { detail: { root: this.element } }),
      );
    }
  }
}
